import React, { useEffect, useState } from "react";
import { Nav, Tab } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import TravelListTable from "./TravelListTable";
import TravelListTopFilter from "./TravelListTopFilter";
import * as HELPER from "@src/config/helper";
import secureStorage from "@src/config/encrypt";
import {
    TravelListRow,
    TravelListStatus,
    TravelListTabFilterParams,
    TRAVEL_LIST_DEFAULT_FILTER_PARAMS,
} from "./travelListData";
import { getTravelLeadList } from "@src/store/slices/travel";
import { useDispatch } from "react-redux";
import FullPageLoader from "@src/common/FullPageLoader";
import { TRAVEL_STATUS_MAP } from "@src/config/constant";

const DEFAULT_STATUS: TravelListStatus = "quotation_shared";

type TravelListTabData = {
    label: string;
    value: number;
    type: TravelListStatus;
    screenTitle?: string;
    filter_params?: TravelListTabFilterParams;
};

const normalizeTravelStatus = (status: string | null): TravelListStatus => {
    if (status === "policy_issued" || status === "issued") {
        return "policy_issued";
    }

    if (status === "submitted") {
        return "submitted";
    }

    return DEFAULT_STATUS;
};

const getDestinations = (lead: any) => {
    const destinations = lead?.destinations || lead?.destination_country || lead?.destination;

    if (Array.isArray(destinations)) {
        return destinations.map((item: any) => item?.label || item?.name || item).filter(Boolean);
    }

    if (typeof destinations === "string") {
        return destinations.split(",").map((item) => item.trim()).filter(Boolean);
    }

    return [];
};

const getDefaultFilters = (statusType: TravelListStatus = DEFAULT_STATUS) => {
    const { from_date, to_date }: any = HELPER.dateReturn("mtd");

    return {
        page_number: 1,
        datetype: "mtd",
        from_date,
        to_date,
        status_type: statusType,
        searchKey: "",
        shared_on_sort: "",
    };
};

function TravelList() {
    const [searchParams, setSearchParams] = useSearchParams();
    const statusTypeParam = normalizeTravelStatus(searchParams.get("status_type"));
    const searchKeyParam = searchParams.get("searchKey") || "";
    const [activeTab, setActiveTab] = useState<TravelListStatus>(statusTypeParam);
    const [selectedFilters, setSelectedFilters] = useState<any>(getDefaultFilters(statusTypeParam));
    const [loading, setLoading] = useState<boolean>(false);
    const [travelList, setTravelList] = useState<TravelListRow[]>([]);
    const [paginationData, setPaginationData] = useState<any>({});
    const [tabList, setTabList] = useState<TravelListTabData[]>([]);
    const dispatch: any = useDispatch();
    const { user_data } = secureStorage.getItem("authAgentUserInfo") || {};

    const getTabFilterParams = (statusType: TravelListStatus, tabs: TravelListTabData[] = tabList) => {
        return tabs.find((item) => item.type === statusType)?.filter_params || TRAVEL_LIST_DEFAULT_FILTER_PARAMS[statusType] || {};
    };

    useEffect(() => {
        const tabParams = getTabFilterParams(statusTypeParam);
        const baseFilters =
            selectedFilters && Object.keys(selectedFilters || {}).length
                ? selectedFilters
                : getDefaultFilters(statusTypeParam);

        // On tab change, keep existing filters (date range, sort, etc.)
        // and only switch the status-related params.
        const updatedFilters = {
            ...baseFilters,
            ...tabParams,
            page_number: 1,
            status_type: statusTypeParam,
            searchKey: searchKeyParam,
        };

        setActiveTab(statusTypeParam);
        setSelectedFilters(updatedFilters);
        getTravelListing(updatedFilters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusTypeParam, searchKeyParam]);

    const updateSearchParams = (filters: any) => {
        const nextParams: any = {};

        if (filters.status_type) nextParams.status_type = filters.status_type;
        if (filters.searchKey) nextParams.searchKey = filters.searchKey;

        setSearchParams(nextParams);
    };

    const getTravelListing = async (params: any) => {
        const filters = { ...params };
        const modifiedParams = { ...params };
        setSelectedFilters(filters);

        delete modifiedParams?.datetype;
        modifiedParams.user_id = user_data?.agency_id || user_data?.id;

        if (!modifiedParams?.from_date) {
            delete modifiedParams.from_date;
        }

        setLoading(true);
        try {
            const response: any = await dispatch(getTravelLeadList(modifiedParams));
            const list = Array.isArray(response?.data?.leads) ? response.data.leads : [];
            const mappedList: TravelListRow[] = list.map((lead: any, index: number) => ({
                id: lead?.id || lead?.lead_id || lead?.travel_lead_id || index + 1,
                customerName: lead?.customer_name || "",
                destinations: getDestinations(lead),
                tripType: lead?.trip_type || "-",
                planName: lead?.plan_name || "-",
                sharedOn: lead?.shared_on || "-",
                submittedOn: lead?.submitted_on || lead?.shared_on || "-",
                issuedOn: lead?.issued_at || "-",
                policyNo: lead?.policy_no || lead?.policyNo || "-",
                status: lead?.status || "-",
                download_policy:
                    lead?.download_policy ||
                    "",
                quotationUrl: lead?.quotation_url || "",
            }));
            const tabData: TravelListTabData[] = Array.isArray(response?.data?.tab_data)
                ? response.data.tab_data.map((tab: any) => ({
                      ...tab,
                      type: normalizeTravelStatus(tab?.type || null),
                      value: +(tab?.value || 0),
                  }))
                : [];
            const tabFilterParams = getTabFilterParams(filters.status_type || activeTab, tabData);

            setTravelList(mappedList);
            setPaginationData(response?.pagination || response?.data?.pagination || {});
            setTabList(tabData);
            setSelectedFilters((prevFilters: any) => ({
                ...prevFilters,
                ...filters,
                ...tabFilterParams,
            }));
        } finally {
            setLoading(false);
        }
    };

    const activeTabHandler = (tab: any) => {
        if (!tab) return;

        const nextTab = tab as TravelListStatus;
        const updatedFilters = {
            ...selectedFilters,
            ...getTabFilterParams(nextTab),
            page_number: 1,
            status_type: nextTab,
        };

        setActiveTab(nextTab);
        setSelectedFilters(updatedFilters);
        updateSearchParams(updatedFilters);
    };

    const setSearchKeyword = (searchKey: string) => {
        setSelectedFilters({
            ...selectedFilters,
            searchKey,
        });
    };

    const handleKeyDown = (event: any) => {
        if (event.key === "Enter") {
            const updatedFilters = {
                ...selectedFilters,
                page_number: 1,
            };

            updateSearchParams(updatedFilters);
            getTravelListing(updatedFilters);
        }
    };

    const updateFilters = (filters: any) => {
        setSelectedFilters(filters);
    };

    const submitFilters = (filterParams: any) => {
        updateSearchParams(filterParams);
        getTravelListing(filterParams);
    };

    const resetFilters = (filterParams: any) => {
        const defaultFilters = getDefaultFilters(activeTab);
        const updatedFilters = {
            ...defaultFilters,
            ...TRAVEL_STATUS_MAP[defaultFilters?.status_type]
        };

       
        setActiveTab(updatedFilters.status_type);
        setSelectedFilters(updatedFilters);
        updateSearchParams(updatedFilters);
        getTravelListing(updatedFilters);
    };

    return (
        <React.Fragment>
            <FullPageLoader show={loading} />
            <div className="container-fluid wap-bg-white travel-list-page">
                <div className="top-heading financer-lead-heding insurance-top-filer-heading d-flex travel-list-heading">
                    <h1>Insurance Application List</h1>
                </div>

                <div className="loan-filter-bg">
                    <div className="top-filter-bg insurance-filter-outer">
                        <TravelListTopFilter
                            selectedFilters={selectedFilters}
                            submitHandler={submitFilters}
                            resetHandler={resetFilters}
                            onFilterChange={updateFilters}
                            getDefaultFilters={() => ({
                                ...getDefaultFilters(activeTab),
                                ...getTabFilterParams(activeTab),
                            })}
                        />
                    </div>

                    <div className="insurnace-appliction-table-outer m-lg-t travel-list-table-outer">
                        <Tab.Container id="travel-list-tabs" defaultActiveKey={activeTab} activeKey={activeTab}>
                            <Nav variant="pills" className="flex-column tab-line nav nav-pills" onSelect={(k) => activeTabHandler(k)}>
                                <div className="tab-list">
                                    {tabList.map((tab) => (
                                        <Nav.Item key={tab.type}>
                                            <Nav.Link eventKey={tab.type}>
                                                {tab.label}
                                                <span className="list-chips">{tab.value || 0}</span>
                                            </Nav.Link>
                                        </Nav.Item>
                                    ))}
                                </div>

                                <div className="travel-list-header-actions">
                                    <fieldset className="search-bx">
                                        <div className="material" title="Search By Customer">
                                            <input
                                                type="text"
                                                placeholder="Search By Customer"
                                                name="travelSearch"
                                                className="form-input lead_search_input"
                                                value={selectedFilters?.searchKey || ""}
                                                onChange={(e) => setSearchKeyword(e.target.value)}
                                                onKeyDown={handleKeyDown}
                                            />
                                            <i className="ic-search"></i>
                                        </div>
                                    </fieldset>
                                    {/* {activeTab === "quotation_shared" && (
                                        <Link to="/create-travel-insurance">
                                            <button className="btn-primary btn-width-auto">
                                                <i className="ic-add"></i>Create Lead
                                            </button>
                                        </Link>
                                    )} */}
                                </div>
                            </Nav>

                            <Tab.Content>
                                <Tab.Pane eventKey={activeTab}>
                                    <TravelListTable
                                        travelList={travelList}
                                        paginationData={paginationData}
                                        getTravelListing={getTravelListing}
                                        selectedFilters={selectedFilters}
                                        activeTab={activeTab}
                                    />
                                </Tab.Pane>
                            </Tab.Content>
                        </Tab.Container>
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default TravelList;
